// 설정 선언 한 장 — 게임 설정(useGameSettings)과 맵·레이어 설정(useMapSettings)의 키·기본값·기기 로컬 여부,
// 그리고 환경설정 창에 행이 있는 키의 화면 메타(tab·section·label·desc·control·min·max·step·unit).
// 훅의 DEFAULT_SETTINGS·LAYER_DEFAULTS·DEVICE_LOCAL_* 와 검색 색인(gen_settings_index.mjs)이 여기서 파생한다.
// import 가 없다 — node 스크립트가 그대로 읽는다. 기기마다 다른 기본값은 default 를 함수로 두고,
// 훅이 기기 문맥(d = { DEVICE_LIMITS, DEVICE_CAPS, IS_MOBILE_PROFILE, VIEW_MUL_DEFAULT })을 넘겨 푼다.
// ⚠ 행 순서 = 기본값 객체의 키 순서 = 저장되는 JSON 의 키 순서. 끼워 넣을 자리를 지킨다.
// ⚠ 화면 메타는 아직 JSX 손글씨와 «같은 글자»를 적어 둔 것이다 — 행을 고치면 여기도 고친다
//   (gen_settings_index.mjs --check 가 라벨이 UI 소스에 있는지 대조한다).

/** 행 배열 → 기본값 객체. 함수 default 는 d(기기 문맥)로 푼다. */
export function defaultsOf(schema, d) {
  const out = {};
  for (const r of schema) out[r.key] = typeof r.default === 'function' ? r.default(d) : r.default;
  return out;
}

/** 기기 로컬 키 — DB 동기화에서 빼 이 브라우저(localStorage)에만 두는 키. */
export const deviceLocalKeysOf = (schema) => schema.filter((r) => r.deviceLocal).map((r) => r.key);

// ── 게임 설정 (useGameSettings · localStorage game_settings_v1 + 계정 DB) ─────────────────
// deviceLocal: 렌더타겟·메모리가 느는 축(PC 에서 켠 값이 폰으로 가면 폰이 죽던 경로), 땅 그림자
// 드리우기(지형 전량이 그림자 패스에 한 번 더), 가로 잠금(폰 값이 PC 로 가면 전체화면에 들어가려 든다).
// ⚠ deviceLocal 키에는 default 가 반드시 있어야 한다 — 기기 마이그레이션이 기본값을 써 넣는다.
export const GAME_SETTINGS_SCHEMA = [
  // 전투
  { key: 'autoFarmRange', default: 60, tab: 'combat', section: '자동사냥', label: '타겟 탐색 반경', control: 'slider', min: 20, max: 120, step: 5, unit: ' u' },
  { key: 'autoAttackRange', default: 30, tab: 'combat', section: '자동사냥', label: '자동공격 사정거리', control: 'slider', min: 10, max: 60, step: 5, unit: ' u' },
  // 가로로 보기 — 화면 회전을 가로로 잠근다. 켜면 전체화면도 같이 들어간다
  // (탭 상태에서는 잠금이 거절된다 — ui/settings/screenOrientation.js). 기기 로컬(DEVICE_LOCAL_KEYS).
  { key: 'landscapeLock', default: false, deviceLocal: true, tab: 'ui', section: '모바일 화면', label: '가로로 보기', control: 'toggle' },
  // 퀘스트를 새로 받으면 상단 길찾기 배너를 스스로 켜나.
  // 배너의 ✕ 가 이 값을 false 로, 퀘스트 상세의 길찾기 버튼이 다시 true 로 만든다.
  { key: 'questNavAuto', default: true },
  // 퀘스트 추적기 접힘 — 카드별 {questId: true} · 전체 접힘 boolean.
  { key: 'questCardCollapsed', default: {} },
  { key: 'questTrackerCollapsed', default: false },
  // 아이템 상세의 아코디언 펼침 — {desc|src|box: true}. 여러 칸 동시 펼침 · 계정 저장.
  // 기본은 전부 접힘.
  { key: 'itemDetailOpen', default: {} },
  // 패널 커스텀 위치 — 인벤토리 등 창 드래그로 옮긴 좌표({x,y}px). null=기본 도킹
  // 위치. 계정 DB에 저장돼(useGameSettings) 다른 기기로 로그인해도 유지됨.
  { key: 'panelPosInventory', default: null },
  { key: 'panelPosQuest', default: null },
  { key: 'panelPosQuestDetail', default: null },
  { key: 'panelPosCraft', default: null },
  { key: 'panelPosKeybind', default: null },
  { key: 'panelPosSettings', default: null },
  { key: 'panelPosSkillPanel', default: null },
  // 퀘스트 목록 탭 — 칸 정의는 questPresent.QUEST_TABS(adventure) + QuestLog 의 「이야기」.
  { key: 'questLogTab', default: 'adventure' },
  // 채팅 상자 위치. null = 기본(좌하단, 바 위).
  { key: 'panelPosChat', default: null },
  // 시계·날씨 칩 위치. 기본 자리는 미니맵 아래.
  { key: 'hudPosClock', default: null },
  // 퀘스트 추적기(좌상단 상시 HUD) 위치. null=기본(left 12 / top 118).
  { key: 'panelPosQuestTracker', default: null },
  // 배경음 아이콘 위치(격자 밖일 때). null = 기본(상단 가운데).
  { key: 'hudPosMusic', default: null },
  { key: 'hudPosStealth', default: null },
  // 배경음 창 · 배경음 필터 판 위치.
  { key: 'panelPosMusic', default: null },
  { key: 'panelPosMusicFilter', default: null },
  // ⚠ 새 좌표 키는 이 스키마와 POS_PANELS(useGameSettings) 양쪽에 넣는다 — 빠지면 화면 밖 클램프를
  //   못 받아 창을 줄인 뒤 다시 열면 영영 안 보이는 자리에 남는다.
  { key: 'panelPosPetPanel', default: null },
  { key: 'panelPosPetDetail', default: null },
  { key: 'panelPosShopPanel', default: null },
  { key: 'panelPosAuctionPanel', default: null },
  { key: 'panelPosMercenaryPanel', default: null },
  { key: 'panelPosMercenaryDetail', default: null },
  { key: 'panelPosGearFitting', default: null },
  { key: 'panelPosSocial', default: null },
  { key: 'panelPosEstate', default: null },
  { key: 'panelPosEstateInfo', default: null },
  { key: 'panelPosQuestCard', default: null },
  { key: 'panelPosVillainRequest', default: null },
  { key: 'panelPosStats', default: null },
  // 전체화면 모달에서 창으로 내린 셋.
  { key: 'panelPosCodex', default: null },
  { key: 'panelPosHelpPanel', default: null },
  { key: 'panelPosQuestPanel', default: null },
  { key: 'panelPosAllFeatures', default: null },
  { key: 'panelPosFeed', default: null },
  { key: 'panelPosVoice', default: null },
  { key: 'panelPosLayers', default: null },
  { key: 'panelPosBuildMode', default: null },
  { key: 'panelPosWarp', default: null },
  { key: 'panelPosTransit', default: null },

  // 패널 세로 크기 — null = 패널 자기 기본값(76vh · 400px 등). 숫자면 그 px 로 고정.
  // 어느 판이 이걸 갖는지는 POS_PANELS 튜플의 3번째 칸이 정한다.
  // ⚠ 가로는 없다 — 격자를 품은 창은 폭을 바꾸면 칸이 잘려 「가로 8칸」 설정이 거짓말을 한다.
  { key: 'panelHQuest', default: null },
  { key: 'panelHSkillPanel', default: null },
  { key: 'panelHSettings', default: null },
  { key: 'panelHKeybind', default: null },
  { key: 'panelHSocial', default: null },

  { key: 'hudPosGold', default: null },
  { key: 'hudPosSpeed', default: null },
  // HUD 격자 배치 — { "<해상도 프로필>": { "<위젯 id>": { a:'tr', c:0, r:0, w:3, h:3 } } }.
  // 프로필이 바깥 열쇠라 27인치와 노트북이 서로 다른 배치를 가진다(ui/hud/hudGrid.js).
  // 빈 객체가 기본 — 배치가 없는 위젯은 종전 도킹/자유좌표 규칙 그대로 그려진다.
  { key: 'hudLayouts', default: {} },
  // HUD 판 배경으로 쓸 복셀 블록 텍스처. `{all, by}` — `by` 는 위젯별 지정(추후 UI).
  { key: 'hudSkin', default: { all: 'none', by: {} } },
  { key: 'panelTint', default: { mode: 'theme', theme: 'game', hue: 262, sat: 45, depth: 50 } },
  // 빠른모드 — 안 쓰는 HUD 아이콘 숨기기. settings·layers 아이콘은 목록에서 제외한다
  // (settings 는 끄면 다시 켤 문이 없는 탈출구, layers 는 순수 렌더 토글).
  { key: 'showSocialIcon', default: true },      // 소셜·유저찾기
  { key: 'showVoiceIcon', default: true },       // 장터말 근거리 음성 진입 아이콘
  { key: 'showStatsIcon', default: false },      // 모바일 시스템 레일의 스탯 팝업 아이콘 (초상 탭이 기본 진입점)
  { key: 'showInventoryIcon', default: true },   // 인벤토리 바로가기
  { key: 'showEquipmentIcon', default: true },
  { key: 'showCraftingIcon', default: true },    // 대장간 제작·강화 바로가기
  { key: 'showQuestIcon', default: true },       // 모바일 시스템 메뉴의 퀘스트 아이콘
  { key: 'showSkillIcon', default: true },       // 모바일 시스템 메뉴의 스킬 목록 아이콘
  { key: 'showCodexIcon', default: true },       // 모바일 시스템 메뉴의 도감 아이콘
  { key: 'showHelpIcon', default: true },        // 인게임 도움말·동행 대화 진입점
  { key: 'showMercenaryIcon', default: true },   // 용병 고용소 바로가기
  // 우측 통합 레일에서 **접었을 때 밖에 남길** 아이콘 id 목록. null = 플랫폼 기본값
  //   (GameOverlay 의 RAIL_PINNED_DEFAULT). show*Icon(빠른모드)과 독립된 축이고,
  //   끈 아이콘은 핀에 들어 있어도 안 나온다.
  //   ⚠ 지금은 읽는 데가 없다 — 레일이 해체되어 「접기」 자체가 사라졌다. 값은 남긴다.
  { key: 'hudRailPinned', default: null },
  { key: 'hudRailCols', default: null },   // (구) 열 고정 — 안 읽는다. 아래 hudRailRows 로 대체
  // 레일 기둥 길이 = 세로 칸 수. null = 자동(화면이 허용하는 만큼). 1~14 정수.
  //   ⚠ 지금은 읽는 데가 없다 — 기둥 길이는 화면 높이에서만 파생한다(railRowsFit).
  { key: 'hudRailRows', default: null },
  // 인벤토리 격자 — cols × rows = 한 페이지 칸수. 넘치면 창이 길어지는 게 아니라
  //   페이지 탭(인벤 1·2·3…)이 생긴다.
  { key: 'invCols', default: 5 },   // 4~8
  { key: 'invRows', default: 6 },   // 4~12
  // 카메라/환경
  { key: 'cameraFov', default: 60, tab: 'camera', section: '시점', label: 'FOV (시야각)', control: 'slider', min: 10, max: 100, step: 1, unit: '°' },           // 10~100, 투시 모드일 때만 적용 — 60° = 게임 표준 (FPS 90, MMO 50~70)
  { key: 'cameraPitch', default: 45, tab: 'camera', section: '시점', label: '내려보는 각도', control: 'slider', min: 30, max: 75, step: 1, unit: '°' },         // 30~75, 투시 모드 내려보는 각도(°). 낮을수록 지평선↑·나무가 캐릭 가림↑, 높을수록 탑다운(전투 시야↑)
  { key: 'cameraTargetLift', default: 1.7, tab: 'camera', section: '시점', label: '시선 높이(조준점)', control: 'slider', min: 0, max: 15, step: 0.1, unit: 'm' },   // 0~15m, 3인칭 조준점을 캐릭터 발에서 이만큼 올린다. 0=화면 정중앙 · 1.7=머리 위 · 8~15=세로 화면에서 캐릭터를 화면 아래로 더 내린다
  // 카메라 회전 감쇠 — 기본 꺼짐. 터치 드래그의 손떨림 지터를 줄인다(플랫폼 분기가 아니라 토글).
  { key: 'cameraDragSmoothing', default: false, tab: 'camera', section: '시점', label: '카메라 회전 부드럽게', desc: '드래그 관성 + 이동 중 손을 놓으면 캐릭터 등 뒤로 카메라가 서서히 따라옵니다', control: 'toggle' },
  // 화면 흔들림(피격 카메라 셰이크) 배율 — 0~1.5, 0=완전히 끔. cameraShake.js
  { key: 'cameraShakeStrength', default: 1, tab: 'camera', section: '시점', label: '화면 흔들림', control: 'slider', min: 0, max: 1.5, step: 0.1, unit: '×' },
  // 크로스헤어 조준 — PC 전용 옵트인. 켜면 조준점이 **화면 정중앙**으로 고정되고 카메라가
  // 옆으로 밀려 캐릭터가 화면 왼쪽에 선다. 전투·채집·굴착이 전부 그 한 점을 쓴다.
  // 기본이 꺼짐인 이유는 aimStore.js 의 aimMode 주석 참조.
  { key: 'aimCrosshair', default: false, tab: 'camera', section: '시점', label: '크로스헤어 조준 (실험)', desc: '캐릭터를 화면 왼쪽으로 밀고, 화면 정중앙 십자선으로 겨눕니다', control: 'toggle' },
  // ⚠ 아래 기본값과 슬라이더의 「권장」 표시(GameSettingsModal)는 **같이** 옮긴다 —
  //   한쪽만 바꾸면 처음 켠 유저에게 「권장과 다른 기본값」이 보인다.
  { key: 'aimShoulder', default: 0.5, tab: 'camera', section: '시점', label: '캐릭터 치우침', control: 'slider', min: 0, max: 8, step: 0.25, unit: 'm' },        // 0~8m, 카메라를 옆으로 미는 거리. 클수록 캐릭터가 왼쪽으로.
  { key: 'aimLift', default: 0.4, tab: 'camera', section: '시점', label: '시선 들기', control: 'slider', min: 0, max: 5, step: 0.1, unit: 'm' },            // 0~5m, 시선을 추가로 들어 올린다. 클수록 십자선이 앞 벌판으로 내려앉는다.
  { key: 'hdriEnabled', default: false },      // 정적 HDRI 폐기(라이브 하늘로 대체) — 레거시 설정 호환용 필드만 유지
  { key: 'hdriPreset', default: 'day_clear' }, // HDRI_PRESETS 키 — 레거시 마이그레이션(구버전 hdriPreset 키 정규화)에만 사용
  // 하늘 렌더 방식 — 'live' 고정(절차 생성 하늘, 24h 시계).
  // 저장된 'hdri' 는 loadLocal() 에서 무조건 'live' 로 강제 마이그레이션된다.
  { key: 'skyMode', default: 'live' },
  // 이동속도
  // 자동 물약 — 켜져 있거나 자동사냥 중이면 HP·MP 가 문턱(%) 아래일 때 가장 작은 물약을 대신 쓴다. 문턱 0 = 그 축 끔.
  //   어떤 물약을 고르는지는 hooks/useAutoPotion.js 머리말.
  { key: 'autoPotionEnabled', default: false, tab: 'combat', section: '자동 물약', label: '자동 물약', desc: 'HP·MP 가 문턱 아래로 내려가면 가진 것 중 가장 작은 물약을 씁니다(3초 간격)', control: 'toggle' },
  { key: 'autoPotionPct', default: 30, tab: 'combat', section: '자동 물약', label: 'HP 문턱', control: 'slider', min: 0, max: 80, step: 10, unit: '%' },
  { key: 'autoPotionMpPct', default: 30, tab: 'combat', section: '자동 물약', label: 'MP 문턱', control: 'slider', min: 0, max: 80, step: 10, unit: '%' },
  // 기본공격 조준 표시(AttackReticle) — PC 전용 aimCrosshair(카메라를 미는 십자선 모드)와 별개다. 모바일에서도 보인다.
  { key: 'attackReticle', default: true, tab: 'combat', section: '조준', label: '기본공격 조준 표시', desc: '기본공격 모드에서 화면 정중앙에 조준 모서리를 그립니다', control: 'toggle' },
  { key: 'attackReticleScale', default: 1, tab: 'combat', section: '조준', label: '조준 크기', control: 'slider', min: 0.75, max: 1.5, step: 0.25, unit: '×' },
  { key: 'attackReticleAlpha', default: 0.3, tab: 'combat', section: '조준', label: '조준 진하기', control: 'slider', min: 0.2, max: 0.8, step: 0.1 },
  { key: 'showMyLocation', default: false, tab: 'ui', section: '지도', label: '나의 위치 공개', control: 'toggle' },       // 자신의 위치를 다른 유저에게 공개 (렌더 레이어가 아니라 "남에게 보일지" 정책 — 레지스트리 대상 아님)
  { key: 'showOtherPlayers', default: true },      // [레거시] minimapLayers.player 로 승계됨(resolveLayerVisibility) — 구 저장값 호환용
  // 미니맵 레이어 가시성 — 키/기본값은 ui/minimap/minimapLayers.js 레지스트리가 정의한다.
  // 여기엔 유저가 명시적으로 바꾼 것만 쌓이므로 레이어가 늘어도 마이그레이션이 필요 없다.
  { key: 'minimapLayers', default: {} },
  // 미니맵 배경 — 'terrain'(기본, 인게임 색과 동일) | 'dark' | 'satellite' | 'atlas'.
  // HUD 미니맵은 이 값과 무관하게 항상 'dark'.
  { key: 'minimapBasemap', default: 'terrain' },
  // 포스트FX — 저사양 대비 토글.
  { key: 'bloomEnabled', default: true },      // 발광 번짐(Bloom). 스킬·불빛 발광
  { key: 'bloomThreshold', default: 0.92 },    // 0.5~1.0 — 이 밝기 넘는 픽셀만 번짐. 높을수록 나무 등 안 번지고 진짜 발광원만
  { key: 'bloomIntensity', default: 0.50 },     // 0~2 — 번짐 세기
  // 그래픽/성능 — 아래 값들은 «이 기기의 기본값»으로 덮인다(useGameSettings 의 Object.assign(DEVICE_CAPS)).
  { key: 'multiDongCap', default: 5, deviceLocal: true },         // 1~5 — 동시 로드 가능한 이웃 동 개수(RpgWorld MAX_NEIGHBOR_DONGS)
  { key: 'dprScale', default: 1.0, deviceLocal: true, tab: 'perf', section: '세부 조정', label: '해상도 배율', control: 'slider', min: 0.5, step: 0.05, unit: '×' },           // 0.5~1.5 — 렌더 해상도 배율(GameCanvas dpr). 낮추면 fill 비용↓
  { key: 'shadowsEnabled', default: true, deviceLocal: true },    // 방향성 소프트 그림자. 저사양(useLowEndGfx)·모바일 프리셋에서는 강제 OFF
  { key: 'vignetteEnabled', default: true },   // 가장자리 시네마틱 어둡게(Vignette). 취향이라 off 가능 — 끄면 화면 코너가 밝고 평평
  // 낮의 생명감 — 꽃가루·나비·새. 밤의 반딧불과 상보라 동시에 켜지지 않는다. 드로콜 3.
  // 저사양 자동 판정(useLowEndGfx)에서는 강제 OFF — 여기 값과 무관하다.
  { key: 'ambientLifeEnabled', default: true },
  // ── 빛·그림자 ─────────────────────────
  // 톤매핑·노출·햇빛세기는 비용 0(픽셀 곱셈)이고 그림자 거리만 실제 비용(캐스터를 한 번 더
  // 그린다) — 그래서 둘을 같은 칸에 두지 않는다. 하늘이 24h 라이브라 라이트맵은 못 쓴다.
  { key: 'toneMapping', default: true },       // ACES 필믹 톤매핑 + sRGB 출력. OFF 면 밝은 영역이 잘려(clip)
                           // 노을의 붉은 금빛이 흰색으로 뭉갠다.
  // ⚠ 아래 빛·색 기본값은 **LIGHT_PRESETS.storybook 과 글자 그대로 같아야 한다.**
  //   활성 프리셋 판정이 값 일치로 되묻는 구조라, 한쪽만 고치면 갓 접속한 유저에게
  //   「아무 프리셋도 안 켜진(사용자 지정)」 화면이 뜬다.
  { key: 'toneExposure', default: 1.02 },      // 0.5~1.8 — 노출. 톤매핑 ON 일 때만 의미 있다(OFF 면 무시)
  { key: 'sunIntensity', default: 1.30 },      // 0.5~2.0 — 태양 세기 배율. ProceduralSky KEY 팔레트 dir 값에 곱한다
  { key: 'ambientLift', default: 0.68 },        // 0.4~1.8 — 환경광 배율. 낮추면 그림자가 깊어지고, 올리면 그늘이 뜬다
  // ── 「밝기」와 「대비」를 가르는 두 축 ────────────────────────
  // 위 둘(햇빛·환경광)은 밝기 손잡이라, 대비를 올리려면 총 밝기까지 같이 흔들린다.
  // 아래 둘이 그 대비 축이다.
  { key: 'lightContrast', default: 0.60 },     // 0~1 — 총 광량은 유지하고 배분만 바꾼다(환경광↓ 만큼 태양↑).
                           // 0=종전과 동일. 레퍼런스(하이테일/마크 셰이더) 룩은 0.4~0.6 대역
  { key: 'shadeTint', default: 0.55 },         // 0~1 — 그늘을 하늘색으로 물들이는 정도(낮에만). 자연광의 그늘은
                           // 회색이 아니라 천공광이다. 0=종전(무채색 그늘). skyLight.SKY_SHADE
  // ── 지면 셰이더 — 둘 다 HeightfieldPoC 의 HF_FRAG 안이다 ──────
  { key: 'formLight', default: 0.60 },         // 0~1 — 형상 명암. 지오메트리에 normal 속성이 없어서 vWorld 화면미분
                           // 외적으로 평면 노멀을 되살려 wrap 라이팅 + 2색 조명. 0=종전 동일
  { key: 'macroVar', default: 0.60 },          // 0~1 — 매크로 색 변화. 48m·160m 저주파를 얹어 「마른 곳 ↔ 짙은
                           // 곳」을 만든다(타일 13.6m 이하만으로는 균일한 초록이 된다). 0=종전 동일
  // ── 컬러 그레이딩 — engine/effects/ColorGrade.jsx ──────────
  { key: 'gradeEnabled', default: true },
  { key: 'gradeSplit', default: 0.85 },         // 0~2 — 그림자 청록 / 하이라이트 황금 분리(split-tone)
  { key: 'gradeContrast', default: 0.30 },     // 0~1 — 중간톤 S-curve. 「디지털 평평함」을 깬다
  { key: 'gradeSat', default: 0.06 },          // -0.5~0.8 — 전역 채도. **중간톤만** 올린다
  { key: 'gradeVibrance', default: 0.34 },     // 0~0.8 — 채도가 이미 높은 색은 덜 올린다(전역 채도와 다른 축)
  { key: 'gradeGreenTame', default: 0.40 },    // 0~1 — 초록만 hue 를 노란쪽으로 + 채도↓
  { key: 'gradeHighlight', default: 0.16 },    // 0~0.8 — 밝은 곳만 밀어 올린다(노출은 전체가 뜬다)
  // 접지 그늘 — AO(주변광 차폐)는 그림자와 다른 축이라 그늘 «안»에서도 생기고, 깊이
  // 기반이라 unlit 바닥에도 먹힌다. 기본 OFF(FPS 영향을 실GPU 없이는 못 가른다) —
  // 깨지면 GlErrorWatch 가 그 자리서 끄고 원문을 남긴다.
  { key: 'ssaoEnabled', default: false, deviceLocal: true },
  // 계단 다듬기(SMAA) — 후처리를 쓰는 순간 하드웨어 MSAA 가 무력해지므로 후처리 AA 가
  // 유일한 길이고, SMAA 는 FXAA 와 달리 텍스처를 안 뭉갠다(픽셀 텍스처 룩에 결정적). 1패스.
  { key: 'aaEnabled', default: true, deviceLocal: true },
  { key: 'shadowStrength', default: 0.68 },    // 0.1~1.0 — 지면 그늘 진하기. 1.0=칠흑, 0.62=하이테일 계열 기본값
  { key: 'shadowDistance', default: 160 },     // 80~400m — 그림자 프러스텀 반경. 160m 는 후암 실 GPU A/B 에서 240m 보다 부하가 낮았다.
  { key: 'groundShadow', default: true },      // 지면(블록지형 LOD 층)이 그림자를 «받는다». 프래그먼트당 샘플 한 번.
                           // OFF 면 나무·건물·캐릭터가 땅에 안 앉아 떠 보인다 — 화면의 대부분이
                           // 바닥이므로 이게 곧 그림자의 대부분이다.
                           // 땅은 `MeshStandardMaterial` + onBeforeCompile 이라 그림자를 그냥 받는다.
  // 땅이 그림자를 «드리운다» — 산이 골짜기에 그늘을 진다. 위와 비용이 자릿수로 다르다:
  //   지형 지오메트리 전량을 그림자 패스에 한 번 더 제출한다(씬에서 가장 큰 메시).
  //   값: 드로콜 +6 · 삼각형 +1.17M(남산 3.07M→4.24M). 프래그먼트가 아니라 제출량이다.
  //   기본값은 기기가 정한다 — pc-high 만 켬(applyTerrainCastShadowV1). 폰은 끔.
  { key: 'terrainCastShadow', default: false, deviceLocal: true },
  // pc-high 승격 도장. 값과 **같은 저장소**(DEVICE_LOCAL_KEYS)에 살아야 한다 —
  //   갈라 두면 PC 에서 찍은 도장이 DB 를 타고 폰으로 가 승격 판정이 어긋난다.
  { key: 'terrainCastShadowV1', default: false, deviceLocal: true },
  { key: 'greenTameV1', default: false, deviceLocal: true },   // applyGreenTameV1 도장
  { key: 'shadowHiV1', default: false, deviceLocal: true },    // applyShadowHiV1 도장
  { key: 'mobileDprV1', default: false, deviceLocal: true },   // applyMobileDprV1 도장
  { key: 'windStrength', default: 1.0 },       // 0~2.5 — 식생 흔들림 세기. 0=완전 정지(종전 나무 상태)
  { key: 'propScale', default: 1.0 },          // 0.6~3.0 — 잔풀·꽃·자갈 크기 배율. 배치 행(placed_objects)을
                           // 안 건드리는 렌더 배율이라 완전 가역
  // 블록 텍스처 해상도. 'auto' = 기기 프로필이 정한다(PC 512 · 폰 256). 숫자면 그 px 로 고정.
  //   ⚠ deviceLocal 키는 default 가 있어야 한다 — 없으면 기기 마이그레이션이 `undefined` 를 써 넣어
  //     설정 화면이 값을 못 읽는다.
  { key: 'blockTexRes', default: 'auto', deviceLocal: true, tab: 'perf', section: '세부 조정', label: '블록 텍스처 해상도', control: 'chips' },
  // 빛 룩 v2 를 이 계정에 한 번 적용했는지 — 값이 아니라 「했음」 기록.
  { key: 'lightLookV2', default: false },
  { key: 'lightLookV3', default: false },
  // 대기(Atmosphere) — 안개색은 저장 안 함(항상 하늘에서 파생 · AtmosphereController).
  { key: 'fogEnabled', default: true },        // 거리 안개 — 원경이 하늘 지평선색으로 녹아 사라짐(동 경계 짤림도 자연 은폐)
  { key: 'fogNear', default: 350 },            // 30~6000m — 안개가 끼기 시작하는 거리(그 안쪽은 완전 선명)
  { key: 'fogFar', default: 800 },             // 200~12000m — 완전히 하늘색이 되는 거리
                           //   상한은 원경 지형(farTerrainFar 2~12km)과 같은 12km 로 맞춘다 —
                           //   어긋나면 근경 안개가 원경보다 먼저 하늘색을 칠한다.
  // ── 원경 지형 ──────────────────────────────────────────────
  // 0 = 끔. 그 외는 하이트필드 다운샘플 배수(32=거침/16=보통/8=고움).
  // 높이는 이미 받아 둔 seoul.global.hf.bin 을 stride 로 읽으므로 추가 다운로드 0.
  { key: 'farTerrainStep', default: 16, deviceLocal: true },      // 0 | 32 | 16 | 8 | 4. 기존 유저는 applyFarTerrainV1 이 올린다
  // ⚠ 승격 도장 키는 **기본값 목록에 반드시 있어야 한다.** DB 로드가
  //   `{...DEFAULT_SETTINGS, ...dbSettings}` 로 병합하므로, 여기 없고 DB 에도 없으면
  //   키가 통째로 사라져 승격이 매 접속마다 다시 돈다 = 유저가 끈 원경이 되살아난다.
  { key: 'farTerrainV1', default: false },     // 1회성 승격 완료 도장 — applyFarTerrainV1 참조
  { key: 'farTerrainV2', default: false },     // 2차 승격 도장 — applyFarTerrainV2 참조
  { key: 'farTerrainFar', default: 6000 },     // 2000~12000m — 원경이 완전히 하늘로 녹는 거리
  // 원경 도시·나무(FarCity) — 원경 지형 **위에 서는** 물건이라, 소비 지점(GameEntry)에서
  //   `farTerrainStep` 과 AND 로 묶는다. 안 묶으면 지형이 꺼진 채 블록 덩어리만 공중에 뜬다.
  //   구운 파일이지만 진입 때 서버가 `world_chunk` 개정과 견줘 다시 굽는다(back/world/far_bake.py).
  //   on/off 는 LAYERS ▸ 원경 이 쥐고, 이 값은 그 아래 단계다.
  { key: 'farCity', default: true },
  // 위 기본값 하향을 **이미 저장된 설정에도 한 번** 먹이는 도장 — applyFarCityOffV1 참조.
  { key: 'farCityOffV1', default: false },
  { key: 'farTerrainLights', default: 1 },     // 0~1.5 — 밤 도심 불빛 세기
  // 안개 끝을 근경 지형 반경에 자동으로 맞춘다 — 켜면 「완전히 가려지는 거리」 슬라이더는
  // 무시되고, 지형이 실제로 깔린 반경이 곧 안개 끝이 된다(반경 끝의 「접시 테두리」를 덮는다).
  { key: 'fogAutoFar', default: true },
  // 원경이 시작되는 반경(m) — 캐릭터로부터의 수평 거리. 0 = 자동(근경 지형이 실제 깔린 반경).
  // 근경 타일이 아직 안 붙었거나 미시공 동이면 그 반경 안이 비므로 그때 손으로 당긴다.
  { key: 'farNearCut', default: 0 },
  // 근경 지형 가장자리를 노이즈로 흩는 폭(m). 0 = 끔(반경 끝에서 뚝 끊긴다).
  // 켜면 원경이 그 톱니 사이를 메우도록 컷이 같이 당겨진다.
  { key: 'rimDither', default: 0 },
  { key: 'cloudShadowEnabled', default: true },   // 구름 그림자 — 하늘 구름(구름량 슬라이더)과 같은 필드가 지면에 그늘
  { key: 'cloudShadowStrength', default: 0.35 },  // 0~0.8 — 그늘 진하기(밤엔 자동 감쇠)
  // 구름 디테일 — 0=낮음(4옥타브) / 1=보통(10, 방향광+권운) / 2=높음(14, +도메인 워프).
  // 하늘 구름과 지면 구름그림자가 같은 값을 봐야 하므로 CLOUD_DETAIL 공유 uniform 경유.
  { key: 'cloudQuality', default: 1 },
  // 키 바인딩 — { actionId: [code, ...] }. **기본값과 다른 것만** 쌓인다.
  // 정의(액션·기본 키)는 data/keymap.js 가 갖는다 — 안 건드린 액션은 새 기본값을 따라간다.
  { key: 'keybinds', default: {} },
  // 사운드 — audioManager(WebAudio 합성 SFX) 연동.
  { key: 'soundMuted', default: false, tab: 'sound', section: '볼륨', label: '음소거', desc: '배경음·효과음 전체 끄기', control: 'toggle' },       // 전체 음소거
  { key: 'sfxVolume', default: 0.8, tab: 'sound', section: '볼륨', label: '효과음 볼륨', control: 'slider', min: 0, max: 1, step: 0.05, unit: '' },          // 0~1 — UI/게이트 효과음
  { key: 'bgmVolume', default: 0.5, tab: 'sound', section: '볼륨', label: '배경음 볼륨', control: 'slider', min: 0, max: 1, step: 0.05, unit: '' },          // 0~1 — 배경음
  // 필드 BGM — **켤 곡의 배열**이 답이다(audioManager.resolveFieldBgm).
  // null = 아직 손댄 적 없음 → 옛 값(fieldBgmTrack)에서 유도한다. 빈 배열 = 배경음 끄기.
  { key: 'bgmTracks', default: null },
  { key: 'fieldBgmTrack', default: 'rotation' }, // [옛 축] 'rotation'|mp3키|'off' — 새로 쓰지 않는다(위 유도용으로만 읽힘)
  // 환경음(루프) — BGM 과 다른 축이라 손잡이도 따로다(같이 켜 두는 게 정상 상태).
  { key: 'ambienceTrack', default: 'off' },      // 'off'|mp3키 (audioManager.AMBIENCE_OPTIONS)
  { key: 'ambienceVolume', default: 0.35, tab: 'sound', section: '환경음', label: '환경음 볼륨', control: 'slider', min: 0, max: 1, step: 0.05, unit: '' },      // 0~1 — 밑에 깔리는 소리라 곡보다 낮게 시작한다
];

// ── 맵·레이어 설정 (useMapSettings · localStorage map_layers_v1 + 계정 DB map_layers) ──────
// deviceLocal: 가시거리·타일 반경은 그 기기가 감당할 양이다(같은 900m 가 PC 에선 풍경, 폰에선 크래시).
export const LAYER_SETTINGS_SCHEMA = [
  { key: 'showCurrentGroupTexture', default: true },
  { key: 'showRoadLayer', default: false },
  { key: 'showRoadStep', default: true },
  // 청크 격자 디버그 오버레이 — 512m 렌더 단위를 바닥에 그린다. 기본 OFF.
  { key: 'showChunkGrid', default: false },
  { key: 'showEstateLayer', default: false },     // 필지 — 16m 땅 칸 선 + 땅 사기
  { key: 'showPropertyLayer', default: false },   // 부동산 — 건물 바닥 경계 + 호버 상세 + 주차 칸
  { key: 'showOperatorBuildScopes', default: false },
  { key: 'showSeedLayer', default: false },          // 시드 «윤곽» — 부술 수 있는 시드 객체 윤곽 + 마당 경계(SeedBreakableLayer)
  { key: 'showSeedBlocks', default: true },          // 시드 블록 — 끄면 시드 마당 칸을 블록 메시에서 뺀다(보기 전용, seedAim.setSeedBlocksHidden)
  // 「구역선」 — 동/구 경계 + 그룹 경계 + 현재 구역 강조를 한 토글로 묶은 평면 오버레이.
  //   drapeToTerrain 으로 지면에 얹는다. 미세선(전 파티션 12,266개)은 무거워서 제외.
  { key: 'showZoneLines', default: false },
  { key: 'showGroupArea', default: false },
  { key: 'showElevation', default: true },    // 고도 표현(평지 → 입체 지형)
  // ── 도로 재질 ──────────────────
  // 자갈은 텍스처가 아니라 셰이더가 좌표에서 뿌린다(텍스처에 그리면 크기가 타일 배율에 묶인다).
  // ⚠ 「자갈·초록·평탄 0 + 타일 13.6」 이 절차 자갈 도입 이전과 픽셀 단위로 같다(롤백 좌표).
  { key: 'roadTileM', default: 6.8 },     // 타일 한 장이 덮는 월드 m — 바탕 흙결의 주기만 정한다
  { key: 'roadFlat', default: 0.45 },    // 바탕 평탄 0~1 — 텍스처를 평균 흙색으로 눌러 구워진 자갈 제거.
                                    //   0 이면 텍스처 자갈 + 절차 자갈이 겹쳐 지저분해진다
  { key: 'roadGravel', default: 0.30 },    // 자갈 밀도 0~1 — 셀 격자에서 이 임계 아래 해시만 자갈이 된다
  { key: 'roadGravelM', default: 0.32 },    // 자갈 셀 크기(m). 알 지름은 대략 이 값의 0.3~0.7배 = 10~22cm
  { key: 'roadGravelVar', default: 0.65 },    // 크기 편차 0~1. 0=전부 같은 크기라 격자로 읽힌다
  { key: 'roadEmbed', default: 0.55 },    // 박힘 0~1. 0=얹힌 돌(밝은 윗면) / 1=파묻힌 돌(접지 그늘 진함)
  { key: 'roadGrass', default: 0.22 },    // 초록 섞임 0~1 — 도로 흙에 **그 자리 잔디색**을 저주파 패치로
                                    //   (상수 초록이 아니라 grass 를 쓴다 — 동마다 잔디 배합이 다르다)
  { key: 'showCliffWalls', default: true },    // 절벽 벽 토글 — 하이트필드 slope 기반 암반 블렌드(hfLayers.cliff)
  { key: 'showHfPoC', default: true },    // 하이트필드가 유일한 지형 소스라 껍데기 토글 — 항상 true 로 강제된다
  // 하이트필드 «메시» — 폐기. 땅을 그리는 층은 블록지형 하나고 그 밖은 원경이다.
  //   하이트필드는 표고 «데이터»(발밑·블록 표고 샘플·물 높이)로만 산다. 이 키는 옛 저장값을
  //   무시하려고 남는다 — 그릴 코드가 소스에 없다.
  { key: 'showHfMesh', default: false },
  // 원경(FarTerrain, 구운 128m 색지도) — 블록지형 반경 «밖»을 받는 유일한 층이다. 켜져 있어야
  //   **땅 밖을 받는 유일한 층**이다. 끄면 블록지형 반경
  //   밖이 하늘이 된다 — 인계선 진단이 아니면 끄지 마라. 삼각형 약 14만·드로콜 1.
  { key: 'showFarWorld', default: true },
  // 블록지형 — 하이트필드 상판을 **1m 블록**으로 갈아끼우는 렌더 층(시공물 도시와 같은
  //   메셔·재질). XZ 해상도가 8m → 1m 로 8배 고와진다.
  //   ⚠ 「청크」(showChunkGrid)와도 다르다 — 그건 바닥 512m 격자 디버그 오버레이다.
  { key: 'showTerrainBlocks', default: true },     // 반경은 기기 프로필이 준다(아래)
  // 블록지형 반경(m) — 발밑부터 이 반경까지를 «칸 LOD_SCALE_M(4)m» 복셀이 그린다.
  //   ⚠ 땅을 그리는 복셀 층은 이것 하나다(1m 층은 없다).
  //   ⚠ 0 이면 층이 통째로 안 선다 — 그때는 하이트필드가 그 자리를 다 그린다(가역).
  //   기기 프로필이 준다 — PC 1024 · 폰 표준 400 · 폰 절전 0(끔).
  { key: 'terrainLodR', default: (d) => d.DEVICE_LIMITS.terrainLodR?.default ?? 0 },
  // ⚠ terrainLodScale 을 여기 다시 넣지 마라 — 축척은 `hfConstants.LOD_SCALE_M` 상수다.
  //   키로 두면 옛 슬라이더(1~16 허용) 시절 localStorage 값이 되살아나 구운 자산과 축척이
  //   갈리고, LOD_STRICT_BAKED 아래에서는 지형이 영구히 0장 그려진다(폴백 없이 무한 대기).
  //   `TerrainLodRender` 는 축척을 prop 으로 안 받고 상수를 직접 쓴다. `pickKnownLayerKeys` 가
  //   이 키를 없는 채로 둬서 옛 저장값도 안 살아난다.
  // ── 블록지형 «룩» 다섯 — 전부 삼각형 0 · 드로콜 0 ────────────
  //   정본은 `entity/world/build/terrainBlockMat.js` 머리말. 모서리 AO 는 그리디 병합을 깨서
  //   삼각형 3.1배라 기각돼 있다. ⚠ 전부 0 이면 셰이더가 이른 반환이라 종전과 동일(가역).
  { key: 'terrainBlockShade', default: 0.55 },   // 면 방향 음영 — 윗면 1.0 · 남북 0.80 · 동서 0.62 · 밑면 0.50
  { key: 'terrainBlockJit', default: 0.32 },   // 칸별 색 지터 — 굽었다(BAKED_LOOK). 슬라이더는 자물쇠
  { key: 'terrainBlockAO', default: 1 },      // 모서리 AO(정점색) — 0 끔 / 1 켬. 켜면 쿼드 ×2.40(실측)
  { key: 'terrainBlockEdge', default: 0.12 },   // 칸 모서리 어둠 — 그리디 병합을 안 깨고 칸 격자를 되살린다
  { key: 'terrainBlockCrack', default: 1.0 },    // 골 그늘 — 알파(높이장)의 «낮은 곳»만 어둡게 (0 끔)
  { key: 'terrainBlockRockMix', default: 0.55 },   // 암반 종 섞기 — 짝 층을 저주파 얼룩으로 (0 끔, 페치 안 늚)
  { key: 'terrainBlockBump', default: 0.6 },    // 암반 범프 — 같은 높이장의 기울기로 노멀을 흔든다 (0 끔)
  { key: 'terrainBlockMacro', default: 0.28 },   // 매크로 반복깨기 — 원경 밉맵이 «평균색 한 판»이 되는 걸 막는다
  { key: 'terrainBlockMacroM', default: 45 },     // 그 파장(m). 짧으면 지저분하고 길면 안 보인다
  // 간선 강조 — 0 이면 종전 그대로(등급별 블록만), 1 이면 간선 상판이 살짝 밝아진다.
  //   「레벨 동선의 척추가 어디냐」를 한눈에 보려는 작업용 눈이다.
  { key: 'terrainRoadEmphasis', default: 0 },
  // ── 지형 팔레트 필터 — 블록지형을 팔레트별로 끈다 ──────────
  //   굽는 자리에서 그 타입의 그룹을 버리므로 삼각형이 «안 만들어진다»(끄면 실제로 싸진다).
  //   ⚠ 「블록」 토글은 `world_block`(사람이 지은 것)이라 여기에 안 닿는다.
  { key: 'showTbGround', default: true },    // 잔디 + 흙 — 자연 지면
  { key: 'showTbStone', default: true },    // 돌 — 절벽·지반
  { key: 'showTbRoad', default: true },    // 길 3종(왕도·읍성길·숲길)
  { key: 'showTbWater', default: true },    // 물 — §물 블록
  // 도로정지 — 길을 폭 방향으로 평평하게 깎는다. 정본은 `roadGrade.js`.
  //   ⚠ 발밑(hfSurfaceYAt)이 같이 움직인다 — 이 토글은 «모양»이 아니라 «땅»을 바꾼다.
  //   ⚠ 폐기 상태 — 기본 false, 실코드도 `hfConstants.ROAD_SPECIAL` 없이는 안 켠다(`?roadspecial=1`).
  { key: 'roadGrade', default: false },
  { key: 'roadGradeR', default: 12 },      // 박스 평균 반경(m). 0=끔. 12~14 에서 이득이 평평해진다
  { key: 'roadGradeCut', default: 1.5 },     // 절토 한도(m) — 산쪽에 설 흙벽의 최대 높이
  { key: 'roadGradeFill', default: 1.5 },     // 성토 한도(m) — 골짜기쪽에 쌓을 둑의 최대 높이
  // ⚠ 밴드 높이는 1m 정수배여야 건축 블록(BLOCK_SIZE=1.0, Y=Math.floor)과 상판 Y 가 맞물린다.
  { key: 'hfBandM', default: 1 },       // 복셀지형 밴드 높이(m). 1m 스냅 권장
  // 경사 적응 밴드 — 테라스 폭 = 밴드 ÷ 경사라 밴드가 같아도 완경사는 한 단이 수십 m 다.
  // 밴드를 줄이는 건 위 1m 정수배 제약 때문에 불가라 경사 양끝을 따로 손본다. 둘 다 0/1 = 종전 동일.
  { key: 'hfFlattenSlope', default: 0.30 },    // 완경사 평탄화: 이 경사(tanθ) 이하는 양자화를 풀어 매끈하게. 0=끔
  { key: 'hfSteepBandMul', default: 1 },       // 급경사 밴드배수: 급한 면일수록 밴드를 정수배(×2·×3)로 키운다. 1=끔
  // X-ray(디버그) — 「판 자리」의 지붕을 벗겨 터널·갱·동굴을 위에서 보이게 한다(판 적 없는 땅은 그대로).
  // ⚠ 실효 반경은 동이 아니라 **정밀반경(hfNearR)** 이다 — 그 밖은 셀이 병합돼 1m 굴착이
  //   메시에 안 올라간다. 멀리가 안 열리면 정밀반경을 올릴 것.
  { key: 'showXrayCut', default: false },
  { key: 'hfVoxAO', default: 0.7 },     // D 코너 AO 강도 — 오목 모서리 음영(마크 smooth lighting). 0=off
  // 임계는 마크 규칙에 맞춘다: 표면 1블록=잔디, 그 아래 3~4블록=흙, 더 아래=돌.
  { key: 'hfVoxStrata', default: 3.0 },     // B 깊이 층리: 흙 두께(m). 이 아래는 암반. 0=off(벽 단일 재질)
  { key: 'hfVoxLip', default: 0.6 },     // C 잔디 옆면 오버레이 두께(m) — 1m 단차는 사실상 전부 잔디/흙
  { key: 'hfVoxAlt', default: 0.45 },    // F 고도 밴딩 강도 — 180~330m 암반, 430~540m 화강암 노출(관악 능선). 0=off
  { key: 'hfVoxSub', default: 1 },       // E 경사 적응 서브셀 배수(1=off, 2=8m→4m, 4=8m→2m). 삼각형 s² 로 증가
  { key: 'hfVoxSubSlope', default: 0.25 },    // E 서브분할 경사 임계(tan) — 이 값 초과 셀만 쪼갠다
  // 「도로서브셀」 — 도로가 지나는 셀만 답판을 1/n 로(roadCells.js). 1=off.
  //   ⚠ 폐기 상태다 — 땅이 이미 world_chunk 1m 블록이라 「길만 다른 눈금」일 이유가 없다.
  { key: 'hfRoadSub', default: 1 },       // 1=off / 2=답판4m / 4=답판2m (0.5m 이상 단 52%→23%→3.7%)
  // 「도로답판 목표단」 — 위 hfRoadSub 을 경사에 필요한 만큼만 쓰게 하는 상한.
  //   s = ceil(경사 × 8m / 이 값) 을 hfRoadSub 로 캡한다(hfConstants.roadSubFor). 0 = 적응 끔.
  //   기준은 통행 한계 STEP_UP=1.1m 다(blockGrid.js — 그 이상은 terrainWall 이 «벽»으로 친다).
  { key: 'hfRoadStepTarget', default: 0.90 },    // m — 도로 답판이 허용하는 잔단 목표. 0=적응 끔
  { key: 'hfNearR', default: 260 },     // 하이트필드 세부: 지형 정밀(풀해상도) 반경(m). 낮추면 근경 삼각형↓·이동 재빌드↓(성능↑), 먼 곳이 일찍 성겨짐
  { key: 'hfTileM', default: 8 },       // 하이트필드 세부: 텍스처 타일 크기(m)
  // 지형 타일(1024m) 「로드해 둘」 반경. DEVICE_LOCAL_LAYER_KEYS 라 계정을 안 넘나든다.
  // ⚠ 렌더는 이 키를 안 읽는다(실제 반경은 `deriveHfTileR`, deviceProfile.js) — 옛 저장 프로필과
  //   촬영 프리셋(originSettings.hfTileR)이 이 이름을 들고 있어 지우면 로드가 깨진다.
  { key: 'hfTileR', default: (d) => d.DEVICE_LIMITS.hfTileR?.default ?? 2048, deviceLocal: true },
  { key: 'hfRelief', default: 0.8 },     // 하이트필드 세부: 절차적 노멀/캐비티 강도 (tile-relief 데모 기법)
  { key: 'hfEdgeSoft', default: 0.14 },    // 하이트필드 세부: 경계 전이 폭 (height-blend 데모의 전이폭)
  { key: 'hfDetile', default: 1 },       // 하이트필드 세부: 스토캐스틱 회전 반복 깨기 강도 (de-tiling 데모 기법)
  { key: 'hfFade', default: 0.6 },     // 하이트필드 세부: 원경 디테일 페이드 강도 (distance-fade 데모 기법)
  { key: 'hfEdgeNoise', default: 0.35 },   // 경계 디더 노이즈(너덜너덜) 강도. 0=부드러운 경계
  { key: 'hfTopMottle', default: 0.04 },   // 상판 명도 얼룩(8m 주기) 진폭. 0 = 얼룩 없음(평균 밝기는 그대로)
  { key: 'hfCliffLo', default: 0.5 },     // 절벽 임계(기울기, tan) — 이 값부터 암반 섞임(+0.45 에서 100%)
  // 디스트릭트 지역색 — theme_code 별 바닥 틴트를 128 중립에서 얼마나 벌릴지. 0=전부 같은 색 /
  // 1=DISTRICTS 표 그대로 / 2=2배 과장. 놓는 순간 컨트롤 캔버스 재페인팅이 돈다(지형 재생성은 아니다).
  { key: 'hfDistrictAmp', default: 1 },
  // 바이옴(동 단위 색 가족) 강도. 배정은 biomePalette.DONG_BIOME 이 갖는다 — 미배정 동은
  // 이 값과 무관하게 그대로다. 0 = 배정된 동까지 바이옴 전(前)으로 복귀.
  { key: 'hfBiomeAmp', default: 1 },
  // 위가 마스터라면 아래 넷은 축별 손잡이다.
  //   회전 = 바닥 색상이 얼마나 도나. ⚠ 기본값은 biomePalette.BIOME_HUE_K 와 **같아야 한다**
  //   채도 = 돌린 색이 얼마나 진한가(0.5 가 limestone 자리)
  //   잎   = 소품이 바닥 가족을 얼마나 따라가나. 0 = 복셀 원래 색(바닥만 갈린다)
  //   전이 = 구 경계를 몇 m 에 걸쳐 넘나. **이것만 유니폼이 아니라 재굽기**(월드 한 장, 0.89 Mpx)
  { key: 'hfBiomeHue', default: 0.6 },
  { key: 'hfBiomeSat', default: 1 },
  // 레이어별 바이옴 추종 강도 — 잔디는 늘 1.0 이고 여기 둘만 눌러
  // 「같은 색 가족 안에서 층이 갈리게」 한다. 0 = 그 층은 바이옴 전(前)으로 돌아간다.
  { key: 'hfBiomeRoad', default: 0.28 },   // 도로 포장 — 사람이 만든 것이라 땅보다 덜 따라간다
  { key: 'hfBiomeRock', default: 0.45 },   // 암반·절벽·고도밴드 — 소품 자갈 STRENGTH 와 같은 값

  { key: 'hfBiomeLeaf', default: 1 },
  { key: 'hfBiomeBlend', default: 45 },
  { key: 'hfBiomeMaterialBlend', default: 90 },
  // 수면 — 품질 0 저(물결·포말·코스틱 끔) / 1 중 / 2 고(기본) / 3 최고(물결이 중경 너머까지).
  //   격자 = 물결·포말을 몇 m 칸으로 계단화할지. 0=매끈, 1~2m 가 건축 블록과 같은 눈금.
  { key: 'waterQuality', default: 2 },
  { key: 'waterVoxel', default: 1.5 },
  // 물가 형상 격자 = 지형 칸(8m) × 이 배율. 0.125 = 1m · 0.25 = 2m · 0 = 옛 폴리곤 곡선.
  //   기기로 값만 가른다 — 1m 는 정점버퍼 21.9MB 라 폰에서 한 단 내린다(드로콜은 눈금과 무관).
  { key: 'waterSnap', default: (d) => d.IS_MOBILE_PROFILE ? 0.25 : 0.125 },
  // 파티션 지역색 블렌딩 반경. **단위는 미터**이고 값이 곧 가우시안 표준편차다
  //   (캔버스가 타일 1024m 를 2048px 로 굽고 CSS blur σ = max(1, 2R)px = max(0.5, R)m).
  //   쓸모 있는 대역은 2.0 근처 — 3.5 이상이면 3m 골목(ROAD_MIN_W)을 색이 넘어와 차단이 무의미하다.
  // ⚠ 마스크 캔버스는 이 값과 무관하게 1.2 고정이다(골목 소실 방지 — HeightfieldPoC 주석).
  { key: 'hfBlendRadius', default: 2.0 },
  { key: 'showRoadColorBarrier', default: true },    // 도로를 색 블렌딩 차단벽으로 쓸지. 끄면 07-31 이전 동작(색이 도로를 넘음)
  { key: 'showPlacedObjects', default: true },    // 배치 오브젝트(placed_objects — GLB 나무·바위·소품) 표시 토글
  // 시공/유저 블록(world_block) 표시 토글 — 위 「오브젝트」와 짝이다.
  // 끄면 InstancedMesh 를 통째로 내린다. 스트리밍·충돌·발밑은 그대로다(PlacedBlocks.jsx 머리말).
  { key: 'showPlacedBlocks', default: true },

  // ── 가시거리 ────────────────────────────────────────────────
  // 「기준 거리 1개 × 종류별 배율」로 컬링 축을 하나로 접는다. 정의·근거는 render/viewDistance.js.
  // ⚠ 아래 objFarCull/objBlobDist/propDist/hfNearR 는 **원본이 아니다** — viewDistance ×
  //   viewMul 에서 파생돼 mapSettings 에 덮여 나간다(저장만 원본 키로 한다).
  // 기준 거리(m) 기본값은 기기 프로파일에서 받는다 — PC 400 · 모바일 260 · 저사양폰 180.
  { key: 'viewDistance', default: (d) => d.DEVICE_CAPS.viewDistance, deviceLocal: true },
  { key: 'viewMul', default: (d) => ({ ...d.VIEW_MUL_DEFAULT }), deviceLocal: true },  // 종류별 배율 — 축은 「실루엣 기여도」

  { key: 'objFarCull', default: 320 },     // 원거리 컬 거리(m). viewMul.object 파생
  { key: 'objBlobDist', default: 90 },      // 복셀 나무 LOD blob 스왑 거리(m). 이 밖 = 거친 블롭(싸다), 안 = 풀 메시
  { key: 'propDensity', default: 0.6 },     // 지면 소품 표시 밀도(0~1). 컷은 id 해시 고정이라 값을 되돌리면 같은 포기가 돌아온다
  { key: 'propDist', default: 55 },      // 지면 소품 표시 거리(m). 나무(320m)와 별개 — 잔풀은 원경 실루엣 기여가 0
  // 접지 그림자 — 지면이 unlit ShaderMaterial 이라 그림자 맵을 원리적으로 못 받는다
  // (HeightfieldPoC 에 lights 가 없다) → 밑동에 원판을 깔아 「붙어 있음」만 만든다.
  { key: 'propShadow', default: 0.5 },     // 지면 소품 접지 그림자 농도(0~1). 0=끔. 나무·바위 그림자는 별개(항상 켬)
  { key: 'worldEditorOpen', default: false },
  // ⚠ **roadTypeFilters 는 아무것도 안 한다** — 토글 버튼 5개가 GameOverlay 에 그려지는데
  //   major/mid/alley/pedestrian/service 를 읽는 도로 렌더러가 0건이다. 살릴 거면 렌더러 배선이 먼저다.
  //   (대조군: 아래 natureFilters.river 는 WorldRivers.jsx 가 실제로 읽는다.)
  { key: 'roadTypeFilters', default: { major: true, mid: true, alley: true, pedestrian: true, service: true } },
  // river = 자연 수계 «전체»(지금은 낙차면만 남았다 — 평수면은 구운 블록이 그린다)
  // fall  = 낙차면(폭포)·물보라 — 블록으로 표현이 안 되는 유일한 물이라
  //         따로 끌 수 있어야 한다(「물」 칩은 구운 블록 물을 쥔다).
  { key: 'natureFilters', default: { river: true, fall: true } },
];

// 환경설정 창에 행이 있지만 게임 설정 저장소에 살지 않는 값 — 검색 색인에만 쓰인다.
//   store: 'map' = useMapSettings 의 카메라 값 · 'local' = 그 창 안에서만 쓰고 저장 안 함
export const SETTINGS_UI_ROWS = [
  { id: 'zoomLevel', store: 'map', tab: 'camera', section: '시점', label: '카메라 거리(줌)', control: 'slider', min: 6, max: 23.5, step: 0.1, unit: '' },
  { id: 'sfxPreviewBoost', store: 'local', tab: 'sound', section: '효과음 미리듣기', label: '미리듣기 볼륨', desc: '여기서만 크게 듣습니다 — 게임 안 효과음 크기는 안 바뀝니다 (저장 안 함)', control: 'slider', min: 1, max: 5, step: 0.5, unit: '×' },
];

// 기능과 함께 폐기된 게임 설정 키 — 저장본에서 턴다(useGameSettings.loadLocal). 기능을 지울 때 한 줄 넣는다.
export const RETIRED_SETTING_KEYS = [
  'adaptiveResolution', 'adaptiveResDefaulted',
  'settingsShowDesc', 'uiScale', 'minimapScale', 'hotbarSlotCount', 'hotbarSlotScale',
  'hotbarHideEmpty', 'hudOrbScale', 'showTopBars', 'showStatPanel', 'showMinimap',
  'showChat', 'showJoystick', 'joystickMode', 'showItemNotif', 'showRegionTitle',
  'showQuestTracker', 'showTargetFrame', 'showGoldChip', 'showSpeedWidget', 'showClock',
  'showMobileOrbs', 'hudBarStyle', 'monsterPlateMode', 'monsterPlateDensity',
  'monsterPlateScale', 'monsterPlateHpText', 'monsterPlateBarScale', 'lootLabelScale',
  'questNavAuto', 'questCardCollapsed', 'questTrackerCollapsed', 'questLogTab',
  'panelPosQuest', 'panelHQuest', 'panelPosQuestDetail', 'panelPosQuestTracker',
];

// 레이어 기본값 재기준화 1회성 마이그레이션 — 실행은 useMapSettings(PENDING_MIGRATIONS·applyLayerOverrides).
// `was` 를 준 항목은 저장값이 정확히 그 옛 기본값일 때만 손댄다. 강제 값은 언제나 현재 기본값이다.
// [늘리는 법] `{ flag, keys: [...], was? }` 한 줄. ⚠ 플래그 이름은 재사용 금지 — 이미 찍힌 유저가 건너뛴다.
export const LAYER_ONE_SHOT_MIGRATIONS = [
  { flag: 'elev_on_v1',         keys: ['showElevation'] },              // 고도 표현 강제 ON(평지→입체 지형)
  { flag: 'vox_strata_v2',      keys: ['hfVoxStrata', 'hfVoxLip'] },    // 복셀 층리 임계 재기준화
  { flag: 'blend_radius_v2',    keys: ['hfBlendRadius'], was: 1.2 },    // 지역색번짐 반경 1.2 → 2.0(m)
  { flag: 'flatten_default_v1', keys: ['hfFlattenSlope'], was: 0 },     // 완경사 평탄화 0 → 0.30
  { flag: 'road_sub_v1',        keys: ['hfRoadSub'], was: 1 },          // 도로서브셀 1(off) → 4(답판 2m)
  { flag: 'road_step_v1',       keys: ['hfRoadStepTarget'], was: 0.18 }, // 도로답판 목표단 0.18 → 0.90(통행 STEP_UP 1.1 기준)
  { flag: 'road_sub_off_v1',    keys: ['hfRoadSub'], was: 4 },           // 「도로 특례」 폐기 — 도로서브셀 4 → 1(off)
  { flag: 'road_grade_off_v1',  keys: ['roadGrade'], was: true },        // 「도로 특례」 폐기 — 도로정지 ON → OFF
  { flag: 'far_on_v1',          keys: ['showFarWorld'], was: false },     // 원경 OFF → ON — 블록층이 하이트필드를 덮으려면 밖을 받을 층이 있어야 한다
  { flag: 'hf_mesh_off_v1',     keys: ['showHfMesh'] },                   // 하이트필드 메시 폐기 — 저장값이 무엇이든 OFF (땅은 블록지형 하나)
];
